import { useState } from "react";
import { calculateRentalAnalysis } from "../utils/rentalAnalysisCalculator";

export default function PropertyDetails({ property, prediction, propertyAnalysis, checklistData, computedValues }) {
  const [showNeighborhoodTooltip, setShowNeighborhoodTooltip] = useState(false);

  const originalPrice = computedValues?.originalPrice || property?.price || property?.asking_price || 0;
  const discountPct = computedValues?.discountPct || 0;
  const discountedPrice = computedValues?.discountedPrice || originalPrice;

  const otherCostsItems = checklistData?.other_costs_items || [];
  const financedCosts = otherCostsItems
    .filter(item => item.payment_type === "financed" && item.cost)
    .reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);

  const analysis = calculateRentalAnalysis({
    property,
    prediction,
    propertyAnalysis,
    mlRent: computedValues?.mlRent || 0,
    agentRent: computedValues?.agentRent || 0,
    userRent: computedValues?.highRent || 0,
    discountedPrice,
    lowRent: computedValues?.lowRent || 0,
    highRent: computedValues?.highRent || 0,
  });

  const capRate = analysis?.highIO?.capRate || 0;
  const irrYear7 = analysis?.highIO?.irrYear7 || 0;

  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #000",
    paddingBottom: "8px",
  };

  const infoRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #eee",
  };

  const linkStyle = {
    color: "#000",
    textDecoration: "none",
    fontWeight: "500",
    padding: "4px 8px",
    border: "1px solid #000",
    borderRadius: "4px",
    transition: "all 0.3s ease",
  };

  return (
    <div style={sectionStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "16px", borderBottom: "2px solid #000", paddingBottom: "8px" }}>
        <h2 style={{ ...headingStyle, marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>Property Details</h2>
        {property.property_type && (
          <span style={{ fontSize: "14px", fontWeight: "600", color: "#666", paddingBottom: "2px" }}>
            {property.property_type}
          </span>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
        {/* Left Column */}
        <div>
          <div style={infoRowStyle}>
            <strong>Address:</strong>
            <span>{property.address}</span>
          </div>
          {property.unit_number && (
            <div style={infoRowStyle}>
              <strong>Unit:</strong>
              <span>{property.unit_number}</span>
            </div>
          )}
          {property.listed_date && (
            <div style={infoRowStyle}>
              <strong>Days on Market:</strong>
              <span>{Math.floor((new Date() - new Date(property.listed_date)) / (1000 * 60 * 60 * 24))} days</span>
            </div>
          )}
          <div style={infoRowStyle}>
            <strong>Listing Price:</strong>
            <span>
              ${originalPrice?.toLocaleString()}
              {property.price_change && property.price_change !== 0 && (
                <span style={{
                  marginLeft: "8px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: property.price_change < 0 ? "#2e7d32" : "#d32f2f"
                }}>
                  {property.price_change > 0 ? "+" : "-"}
                  ${Math.abs(property.price_change) >= 1000
                    ? `${(Math.abs(property.price_change) / 1000).toFixed(0)}K`
                    : Math.abs(property.price_change).toLocaleString()}
                  {property.price_change_pct && ` (${property.price_change > 0 ? "+" : "-"}${Math.abs(property.price_change_pct).toFixed(1)}%)`}
                </span>
              )}
            </span>
          </div>
          {discountPct > 0 && (
            <div style={infoRowStyle}>
              <strong>Discount %:</strong>
              <span>{discountPct.toFixed(1)}%</span>
            </div>
          )}
          {financedCosts > 0 && (
            <div style={infoRowStyle}>
              <strong>Other Costs (Financed):</strong>
              <span style={{ color: "#dc3545", fontWeight: "600" }}>+${financedCosts.toLocaleString()}</span>
            </div>
          )}
          {(discountPct > 0 || financedCosts > 0) && (
            <div style={infoRowStyle}>
              <strong>Final Asking Price:</strong>
              <span style={{ color: "#28a745", fontWeight: "600", fontSize: "16px" }}>${discountedPrice.toLocaleString()}</span>
            </div>
          )}
          <div style={infoRowStyle}>
            <strong>Size:</strong>
            <span>{property.sqft} sqft, {property.bedrooms}BR/{property.bathrooms}BA</span>
          </div>
          {property.price && property.sqft && (
            <div style={infoRowStyle}>
              <strong>Price per sqft:</strong>
              <span>${Math.round(property.price / property.sqft).toLocaleString()}</span>
            </div>
          )}
          <div style={infoRowStyle}>
            <strong>Neighborhood:</strong>
            <span>
              {property.neighborhood}
              {(property.neighborhood_avg_cap_rate || property.neighborhood_weighted_avg_cap_rate) && (
                <span
                  style={{ marginLeft: "8px", display: "inline-block", position: "relative" }}
                  onMouseEnter={() => setShowNeighborhoodTooltip(true)}
                  onMouseLeave={() => setShowNeighborhoodTooltip(false)}
                >
                  <span style={{
                    display: "inline-block", width: "16px", height: "16px", borderRadius: "50%",
                    backgroundColor: "#1976d2", color: "#fff", fontSize: "12px", fontWeight: "bold",
                    textAlign: "center", lineHeight: "16px", cursor: "help"
                  }}>i</span>
                  {showNeighborhoodTooltip && (
                    <div style={{
                      position: "absolute", top: "25px", left: "50%", transform: "translateX(-50%)",
                      backgroundColor: "#333", color: "#fff", padding: "12px 16px", borderRadius: "6px",
                      fontSize: "13px", lineHeight: "1.6", whiteSpace: "nowrap",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 1000, pointerEvents: "none"
                    }}>
                      <div style={{ fontWeight: "bold", marginBottom: "8px", borderBottom: "1px solid #555", paddingBottom: "6px" }}>
                        Neighborhood Statistics
                      </div>
                      {property.neighborhood_weighted_avg_cap_rate && (
                        <div style={{ marginBottom: "4px" }}>
                          <strong>Weighted Avg Cap Rate:</strong> {property.neighborhood_weighted_avg_cap_rate.toFixed(2)}%
                        </div>
                      )}
                      {property.neighborhood_avg_cap_rate && (
                        <div style={{ marginBottom: "4px" }}>
                          <strong>Simple Avg Cap Rate:</strong> {property.neighborhood_avg_cap_rate.toFixed(2)}%
                        </div>
                      )}
                      {property.neighborhood_median_cap_rate && (
                        <div style={{ marginBottom: "4px" }}>
                          <strong>Median Cap Rate:</strong> {property.neighborhood_median_cap_rate.toFixed(2)}%
                        </div>
                      )}
                    </div>
                  )}
                </span>
              )}
            </span>
          </div>
          {property.floor && (
            <div style={infoRowStyle}><strong>Floor:</strong><span>{property.floor}</span></div>
          )}
          {property.floor_line && (
            <div style={infoRowStyle}><strong>Floor Line:</strong><span>{property.floor_line}</span></div>
          )}
          <div style={infoRowStyle}>
            <strong>Washer/Dryer:</strong>
            <span>{property.washer_dryer === true ? 'Yes' : property.washer_dryer === false ? 'No' : '-'}</span>
          </div>
        </div>

        {/* Right Column */}
        <div>
          <div style={infoRowStyle}>
            <strong>{property?.property_type?.toLowerCase() === 'coop' || property?.property_type?.toLowerCase() === 'condop' ? 'Monthly Maintenance Fee:' : 'Monthly HOA:'}</strong>
            <span>${property?.monthly_hoa?.toLocaleString() || 0}</span>
          </div>
          {property?.property_type?.toLowerCase() !== 'coop' && property?.property_type?.toLowerCase() !== 'condop' && (
            <div style={infoRowStyle}>
              <strong>Monthly Tax:</strong>
              <span>${property?.monthly_tax?.toLocaleString() || 0}</span>
            </div>
          )}
          {capRate > 0 && (
            <div style={infoRowStyle}>
              <strong>Cap Rate:</strong>
              <span style={{ color: "#28a745", fontWeight: "600" }}>{capRate.toFixed(2)}%</span>
            </div>
          )}
          {property?.neighborhood_avg_cap_rate && capRate > 0 && (
            <div style={infoRowStyle}>
              <strong>RCR:</strong>
              <span style={{ color: "#0066cc", fontWeight: "600" }}>{(capRate / property.neighborhood_avg_cap_rate).toFixed(2)}</span>
            </div>
          )}
          {property?.neighborhood_weighted_avg_cap_rate && capRate > 0 && (
            <div style={infoRowStyle}>
              <strong>Weighted RCR:</strong>
              <span style={{ color: "#0066cc", fontWeight: "600" }}>{(capRate / property.neighborhood_weighted_avg_cap_rate).toFixed(2)}</span>
            </div>
          )}
          {irrYear7 > 0 && (
            <div style={infoRowStyle}>
              <strong>IRR (Year 7):</strong>
              <span style={{ color: "#28a745", fontWeight: "600" }}>{irrYear7.toFixed(2)}%</span>
            </div>
          )}
          {(property.listing_url || property.streeteasy_url) && (
            <div style={infoRowStyle}>
              <strong>Web Listing:</strong>
              <a
                href={property.listing_url || property.streeteasy_url}
                target="_blank"
                rel="noopener noreferrer"
                style={linkStyle}
                onMouseEnter={(e) => { e.target.style.backgroundColor = "#000"; e.target.style.color = "#fff"; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#000"; }}
              >View Listing</a>
            </div>
          )}
          <div style={infoRowStyle}>
            <strong>Dishwasher:</strong>
            <span>{property.dishwasher === true ? 'Yes' : property.dishwasher === false ? 'No' : '-'}</span>
          </div>
          {property.renovation && (
            <div style={infoRowStyle}><strong>Renovation:</strong><span>{property.renovation}</span></div>
          )}
          {property.views && (
            <div style={infoRowStyle}><strong>Views:</strong><span>{property.views}</span></div>
          )}
        </div>
      </div>
    </div>
  );
}
